import React, { useContext, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaShoppingCart, FaTimes, FaFire, FaHome, FaBox, FaCannabis, FaQuestionCircle, FaPhone } from 'react-icons/fa';
import { CartContext } from './CartContext';
import '../css/Navbar.css';

const Navbar = () => {
    const { cart } = useContext(CartContext);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    
    const cartCount = cart.reduce((total, item) => total + item.quantity, 0);

    useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 10;
            setScrolled(isScrolled);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    // Scroll to top of page
    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    // Function to handle navigation with smooth scroll
    const handleNavigation = (path, sectionId = null) => {
        closeMobileMenu();
        
        if (sectionId && location.pathname === '/') {
            // If we're on home page and want to scroll to a section
            setTimeout(() => {
                const section = document.getElementById(sectionId);
                if (section) {
                    section.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            }, 100);
        } else if (path === '/') {
            // If navigating to home page, scroll to top
            setTimeout(() => {
                scrollToTop();
            }, 100);
        }
    };

    // Handle logo click - always go to top of home page
    const handleLogoClick = () => {
        closeMobileMenu();
        if (location.pathname === '/') {
            scrollToTop();
        }
    };

    return (
        <>
            <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
                <div className="nav-container">
                    <div className="nav-brand">
                        <Link 
                            to="/" 
                            className="logo-link" 
                            onClick={handleLogoClick}
                        >
                            <FaCannabis className="logo-icon" />
                            <span className="logo-text">PotCityDC</span>
                        </Link>
                    </div>
                    
                    {/* Desktop Navigation */}
                    <div className="nav-links">
                        <Link 
                            to="/" 
                            className="nav-link" 
                            onClick={() => handleNavigation('/', 'hero-section')}
                        >
                            <FaHome className="nav-icon" />
                            <span className="nav-text">Home</span>
                        </Link>
                        <Link 
                            to="/" 
                            className="nav-link" 
                            onClick={() => handleNavigation('/', 'products-section')}
                        >
                            <FaBox className="nav-icon" />
                            <span className="nav-text">Products</span>
                        </Link>
                        <Link 
                            to="/deals" 
                            className="nav-link deals-link" 
                            onClick={() => handleNavigation('/deals')}
                        >
                            <FaFire className="nav-icon" />
                            <span className="nav-text">Hot Deals</span>
                        </Link>
                        <Link 
                            to="/faq" 
                            className="nav-link" 
                            onClick={() => handleNavigation('/faq')}
                        >
                            <FaQuestionCircle className="nav-icon" />
                            <span className="nav-text">FAQ</span>
                        </Link>
                    </div>

                    <div className="nav-right">
                        {/* Desktop Contact Phone - Hidden on mobile */}
                        <div className="desktop-contact">
                            <a 
                                href="tel:2022099605" 
                                className="contact-link"
                                onClick={closeMobileMenu}
                            >
                                <FaPhone className="contact-icon" />
                                <span className="contact-text">(202) 209-9605</span>
                            </a>
                        </div>

                        {/* Desktop Cart */}
                        <div className="nav-cart">
                            <Link 
                                to="/cart" 
                                className="cart-link" 
                                onClick={() => handleNavigation('/cart')}
                            >
                                <FaShoppingCart className="cart-icon" />
                                {cartCount > 0 && (
                                    <span className="cart-count">{cartCount}</span>
                                )}
                            </Link>
                        </div>

                        {/* Mobile Cart Icon - Visible on mobile screens */}
                        <div className="mobile-cart-icon">
                            <Link 
                                to="/cart" 
                                className="cart-link" 
                                onClick={() => handleNavigation('/cart')}
                            >
                                <FaShoppingCart className="cart-icon" />
                                {cartCount > 0 && (
                                    <span className="cart-count">{cartCount}</span>
                                )}
                            </Link>
                        </div>

                        {/* Mobile Menu Button */}
                        <button 
                            className={`mobile-menu-btn ${isMobileMenuOpen ? 'open' : ''}`}
                            onClick={toggleMobileMenu}
                            aria-label="Toggle menu"
                        >
                            <span></span>
                            <span></span>
                            <span></span>
                        </button>
                    </div>
                </div>

                {/* Mobile Menu Overlay */}
                <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={closeMobileMenu}></div>

                {/* Mobile Menu */}
                <div className={`mobile-menu ${isMobileMenuOpen ? 'active' : ''}`}>
                    <div className="mobile-menu-header">
                        <Link 
                            to="/" 
                            className="mobile-logo" 
                            onClick={handleLogoClick}
                        >
                            <FaCannabis className="logo-icon" />
                            <span className="logo-text">PotCityDC</span>
                        </Link>
                        <button className="mobile-close-btn" onClick={closeMobileMenu}>
                            <FaTimes />
                        </button>
                    </div>
                    
                    <div className="mobile-nav-links">
                        <Link 
                            to="/" 
                            className="mobile-nav-link" 
                            onClick={() => handleNavigation('/', 'hero-section')}
                        >
                            <FaHome className="nav-icon" />
                            <span className="nav-text">Home</span>
                        </Link>
                        <Link 
                            to="/" 
                            className="mobile-nav-link" 
                            onClick={() => handleNavigation('/', 'products-section')}
                        >
                            <FaBox className="nav-icon" />
                            <span className="nav-text">Products</span>
                        </Link>
                        <Link 
                            to="/deals" 
                            className="mobile-nav-link deals-link" 
                            onClick={() => handleNavigation('/deals')}
                        >
                            <FaFire className="nav-icon" />
                            <span className="nav-text">Hot Deals</span>
                            {/* <span className="deal-badge">HOT</span> */}
                        </Link>
                        <Link 
                            to="/faq" 
                            className="mobile-nav-link" 
                            onClick={() => handleNavigation('/faq')}
                        >
                            <FaQuestionCircle className="nav-icon" />
                            <span className="nav-text">FAQ</span>
                        </Link>
                    </div>

                    <div className="mobile-contact-section">
                        <a 
                            href="tel:2022099605" 
                            className="mobile-contact-link"
                            onClick={closeMobileMenu}
                        >
                            <FaPhone className="contact-icon" />
                            <span className="contact-text">Call Us: (202) 209-9605</span>
                        </a>
                    </div>

                    <div className="mobile-cart-section">
                        <Link 
                            to="/cart" 
                            className="mobile-cart-link" 
                            onClick={() => handleNavigation('/cart')}
                        >
                            <FaShoppingCart className="cart-icon" />
                            <span className="nav-text">Shopping Cart</span>
                            {cartCount > 0 && (
                                <span className="mobile-cart-count">{cartCount}</span>
                            )}
                        </Link>
                    </div>
                </div>
            </nav>
        </>
    );
};

export default Navbar;